import { useContext, useEffect, useMemo, useState } from 'react';
import { Portal, svgSprite } from '../../hybrid/wrappers';
import UserContext, { UserProfileContext } from '../../common/utils/getContext';
import { getData } from '../../common/utils/router';
import css from './ActivateAccountModal.module.scss';
import T from '../../common/components/T';
import classNames from 'classnames';
import FormRadio from '../form/components/form-radio/FormRadioSwitcher/FormRadioSwitcher';
import Loader from '../plug/Loader/Loader';
import { packageThead, productThead, transformPackages, transformProducts } from './helpers';
import GridFilter from '../gridFilter/GridFilter/GridFilter';
import { API_SERVER_URL, generateUniqueId } from '../../common/utils/utils';
import { LangContext } from '../../common/decorators/Language';
import SingleButton from '../form/form-control-buttons/SingleButton/SingleButton';
// import ProgressBar from './components/ProgressBar/ProgressBar';

const ActivateAccountModal = ({ closeHandler, confirmFunction, value = {}, country, isOfficeRegister, initValue }) => {
    const { translation } = useContext(LangContext);
    const { userToken, lang, localization } = useContext(UserContext);
    const { profileData, loaded } = useContext(UserProfileContext);
    const [packages, setPackages] = useState(null);
    const [products, setProducts] = useState(null);

    const [initValueSetted, setInitValueSetted] = useState(false);

    const [selectedTable, setSelectedTable] = useState(value?.type ? (value?.type === 'set' ? 0 : 1) : 0);
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        const country_id = country?.geoname_id;
        if (loaded && (initValue?.country_id?.currency || profileData?.currency?.code || localization?.currency)) {
            const promises = [
                getData(
                    `/api/v1/shop/products?page[size]=999&filter[type]=set&filter[product_availability]=in_stock,expected_arrival${
                        country_id ? `&filter[country_id]=${country_id}` : ''
                    }`,
                    userToken,
                    lang,
                    false,
                    isOfficeRegister ? initValue?.country_id?.currency : profileData?.currency?.code || localization?.currency
                ),
                getData(
                    `/api/v1/shop/products?filter[is_package]=0&filter[type]=product&filter[is_additional]=0&page[size]=999&filter[product_availability]=in_stock,expected_arrival${
                        country_id ? `&filter[country_id]=${country_id}` : ''
                    }`,
                    userToken,
                    lang,
                    false,
                    isOfficeRegister ? initValue?.country_id?.currency : profileData?.currency?.code || localization?.currency
                )
            ];
            Promise.all(promises).then(([packages, products]) => {
                if (packages.data) {
                    setPackages({
                        thead: packageThead,
                        tbody: transformPackages([], translation)(packages)
                    });
                }
                if (products.data) {
                    setProducts({
                        thead: productThead,
                        tbody: transformProducts()(products)
                    });
                }
            });
        }
    }, [loaded, userToken, lang, country, profileData, localization?.currency]);

    const clearValue = () => {
        setPackages((state) => {
            return {
                ...state,
                tbody: {
                    ...state?.tbody,
                    data: state?.tbody?.data?.map((it) => {
                        const isSelected = selectedItems?.find((i) => i?.id === it?.item?.id);
                        return {
                            ...it,
                            radio: !!isSelected,
                            activeRow: !!isSelected
                        };
                    })
                }
            };
        });
        setProducts((state) => {
            return {
                ...state,
                tbody: {
                    ...state?.tbody,
                    data: state?.tbody?.data?.map((it) => {
                        const isSelected = selectedItems?.find((i) => i?.id === it?.item?.id);
                        return {
                            ...it,
                            activeRow: !!isSelected,
                            quantity: isSelected?.quantity || 1
                        };
                    })
                }
            };
        });
    };

    const buttonAvailable = useMemo(() => {
        if (selectedItems?.length) return true;
    }, [selectedItems, selectedTable]);

    useEffect(() => {
        if (value?.items?.length && packages && products && !initValueSetted) {
            setSelectedItems(value?.items);
            setPackages((state) => {
                return {
                    ...state,
                    tbody: {
                        ...state?.tbody,
                        data: state?.tbody?.data?.map((it) => {
                            const isSelected = value?.items?.find((i) => i?.id === it?.item?.id);
                            return {
                                ...it,
                                radio: !!isSelected,
                                activeRow: !!isSelected
                            };
                        })
                    }
                };
            });
            setProducts((state) => {
                return {
                    ...state,
                    tbody: {
                        ...state?.tbody,
                        data: state?.tbody?.data?.map((it) => {
                            const isSelected = value?.items?.find((i) => i?.id === it?.item?.id);
                            return {
                                ...it,
                                activeRow: !!isSelected,
                                quantity: isSelected?.quantity || 1
                            };
                        })
                    }
                };
            });
            setInitValueSetted(true);
        }
    }, [value?.items, packages, products, initValueSetted]);

    const quantityHandler = (product, type) => {
        if (type === 'minus') {
            if (product?.quantity === 1) return;
            setProducts((state) => ({
                ...state,
                tbody: {
                    ...state?.tbody,
                    data: state?.tbody?.data?.map((it) => {
                        if (it?.item?.id === product?.item?.id) {
                            return {
                                ...it,
                                quantity: product?.quantity - 1
                            };
                        }
                        return it;
                    })
                }
            }));
            if (product.activeRow) {
                setSelectedItems((state) =>
                    state?.map((it) => {
                        if (it.id === product?.item?.id) {
                            return {
                                ...it,
                                quantity: product?.quantity - 1
                            };
                        }
                        return it;
                    })
                );
            }
        } else if (type === 'plus') {
            setProducts((state) => ({
                ...state,
                tbody: {
                    ...state?.tbody,
                    data: state?.tbody?.data?.map((it) => {
                        if (it?.item?.id === product?.item?.id) {
                            return {
                                ...it,
                                quantity: product?.quantity + 1
                            };
                        }
                        return it;
                    })
                }
            }));
            if (product.activeRow) {
                setSelectedItems((state) =>
                    state?.map((it) => {
                        if (it.id === product?.item?.id) {
                            return {
                                ...it,
                                quantity: product?.quantity + 1
                            };
                        }
                        return it;
                    })
                );
            }
        }
    };

    return (
        <Portal closeHandler={closeHandler}>
            <div className={css.modal}>
                {!packages || !products ? (
                    <Loader />
                ) : (
                    <>
                        <div onClick={closeHandler} className={classNames('circleButton', css.closeModal)}>
                            {svgSprite('cross')}
                        </div>
                        <p className={css.modalTitle}>
                            <T textName='activate-account-modal-title' defaultStr='Выбор товара для активации аккаунта' />
                        </p>
                        <p className={css.modalSubtitle}>
                            <T textName='activate-account-modal-subtitle-v2' defaultStr='Выберите готовый пакет или товары' />
                        </p>
                        <FormRadio
                            options={[
                                <T key={generateUniqueId()} textName='package' defaultStr='Пакет' />,
                                <T key={generateUniqueId()} textName='product2' defaultStr='Товар' />
                            ]}
                            value={selectedTable}
                            compStyle={{
                                wrapper: css.switcherWrapper,
                                content: css.switcher
                            }}
                            setFormData={(attribute, value) => {
                                setSelectedItems([]);
                                clearValue();
                                setSelectedTable(value);
                            }}
                        />
                        {/* {selectedTable === 1 ? (
                            <ProgressBar
                                // setEnoughProductsForPackage={setEnoughProductsForPackage}
                                packages={fullPackages}
                                selectedItems={selectedItems}
                            />
                        ) : null} */}
                        <GridFilter
                            noHeader={true}
                            sticky={false}
                            grid={{
                                initData: selectedTable === 0 ? packages : products,
                                setInitData: selectedTable === 0 ? setPackages : setProducts,
                                gridStyles: {
                                    gridWrapper: {
                                        className: css.gridWrapper
                                    },
                                    activeRow: {
                                        td: {
                                            className: css.activeRow
                                        }
                                    },
                                    price: {
                                        td: {
                                            style: {
                                                whiteSpace: 'nowrap'
                                            }
                                        }
                                    }
                                },
                                handlers: {
                                    tr: {
                                        onClick: (row) => {
                                            if (selectedTable === 0) {
                                                setSelectedItems([row.item]);
                                                setPackages((state) => ({
                                                    ...state,
                                                    tbody: {
                                                        ...state?.tbody,
                                                        data: state?.tbody?.data?.map((pack) => {
                                                            if (row?.item?.id === pack?.item?.id) {
                                                                return {
                                                                    ...pack,
                                                                    radio: true,
                                                                    activeRow: true
                                                                };
                                                            }
                                                            return {
                                                                ...pack,
                                                                radio: false,
                                                                activeRow: false
                                                            };
                                                        })
                                                    }
                                                }));
                                            }
                                        }
                                    },
                                    quantity: {
                                        plus: {
                                            onClick: (row) => {
                                                quantityHandler(row, 'plus');
                                            }
                                        },
                                        minus: {
                                            onClick: (row) => {
                                                quantityHandler(row, 'minus');
                                            }
                                        }
                                    },
                                    activeRow: {
                                        change: {
                                            onChange: (row) => {
                                                const newRow = {
                                                    ...row?.item,
                                                    quantity: row?.quantity
                                                };

                                                if (row.activeRow) {
                                                    setSelectedItems((state) => state.filter((it) => it.id !== row?.item?.id));
                                                } else {
                                                    setSelectedItems((state) => [...state, newRow]);
                                                }
                                                setProducts((state) => ({
                                                    ...state,
                                                    tbody: {
                                                        ...state?.tbody,
                                                        data: state?.tbody?.data?.map((it) => {
                                                            if (it?.item?.id === row?.item?.id) {
                                                                return {
                                                                    ...it,
                                                                    activeRow: !it.activeRow
                                                                };
                                                            }
                                                            return it;
                                                        })
                                                    }
                                                }));
                                            }
                                        }
                                    }
                                }
                            }}
                            customStyles={css}
                            filter={{
                                data: () => [],
                                currency: isOfficeRegister ? initValue?.country_id?.currency : profileData?.currency?.code,
                                url:
                                    selectedTable === 0
                                        ? `${API_SERVER_URL}/api/v1/shop/products`
                                        : `${API_SERVER_URL}/api/v1/shop/products`,
                                filterParams:
                                    selectedTable === 0
                                        ? 'filter[is_package]=1&filter[product_availability]=in_stock,expected_arrival'
                                        : 'filter[is_package]=0&page[size]=999&filter[product_availability]=in_stock,expected_arrival'
                            }}
                            textsData={{}}
                            convertData={
                                selectedTable === 0
                                    ? transformPackages(selectedItems, translation)
                                    : transformProducts(selectedItems, false)
                            }
                            showBy={{
                                options: [999],
                                value: 0
                            }}
                        />
                        <SingleButton
                            type='ok'
                            buttonText={<T textName='confirm' defaultStr='Подтвердить' />}
                            customButton={{
                                singleButton: {
                                    className: css.confirmButton
                                }
                            }}
                            disabled={!buttonAvailable}
                            onTouchEndEvent={false}
                            clickFunc={() => {
                                confirmFunction &&
                                    confirmFunction({ type: selectedTable === 0 ? 'set' : 'product', items: selectedItems });
                                closeHandler();
                            }}
                        />
                    </>
                )}
            </div>
        </Portal>
    );
};

export default ActivateAccountModal;
